export const GUARDIAN_OVERDUE_PAY_MESSAGE = 'Guardian acknowledgment overdue — $25 rate; excluded from volume.';
export const GUARDIAN_CLOCK_MISSING_MESSAGE = 'Enter the encounter date and session end time before the guardian deadline can be calculated.';
export const GUARDIAN_SIGNED_AT_UNREADABLE_MESSAGE = 'The guardian signing time cannot be read. Correct that timestamp before pay can use the normal rate.';

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_RE = /^(\d{2}):(\d{2})(?::(\d{2}))?$/;
const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

export interface GuardianPayWindow {
  missing: boolean;
  unreadableSignedAt: boolean;
  isLate: boolean;
  isSigned: boolean;
  isWaived: boolean;
  sessionEndIso?: string;
  deadlineMs?: number;
  deadlineLabel?: string;
  completedAt?: string;
  elapsedHours?: number;
  reason?: string;
}

function parseStoredInstant(text: string): number {
  let normalized = text.trim();
  if (!normalized.includes('T')) normalized = normalized.replace(' ', 'T');
  normalized = normalized.replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
  normalized = normalized.replace(/([+-]\d{2})$/, '$1:00');
  const parsed = Date.parse(normalized);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
}

function clock(partial: Partial<GuardianPayWindow> & Pick<GuardianPayWindow, 'missing' | 'isLate' | 'isSigned'>): GuardianPayWindow {
  return {
    unreadableSignedAt: false,
    isWaived: false,
    ...partial
  };
}

export function formatArizonaDeadline(deadlineMs: number): string {
  const label = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Phoenix',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short'
  }).format(new Date(deadlineMs));
  return label.includes('Arizona') ? label : `${label} Arizona time`;
}

/** A stored instant whose offset or Z suffix is already present. A bare date or clock is left unchanged. */
function hasExplicitSourceZone(value: string): boolean {
  return /(?:T|\s)\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}(?::?\d{2})?)$/i.test(value);
}

/**
 * Display an already zoned instant in America/Phoenix, keeping the stored fractional seconds.
 * Values without an explicit offset or Z are returned unchanged.
 */
export function formatArizonaTimestamp(value?: string | null): string {
  const raw = String(value ?? '').trim();
  if (!raw || !hasExplicitSourceZone(raw)) return raw;
  const fraction = raw.match(/\.(\d+)/)?.[1] ?? '';
  let normalized = raw.replace(' ', 'T');
  normalized = normalized.replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
  normalized = normalized.replace(/([+-]\d{2})$/, '$1:00');
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return raw;
  const label = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Phoenix',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZoneName: 'short'
  }).format(parsed);
  const withFraction = fraction
    ? label.replace(/(\d{2})(?=\s?(?:AM|PM)\b)/, `$1.${fraction}`)
    : label;
  return withFraction.includes('Arizona') ? withFraction : `${withFraction} Arizona time`;
}

export function assessGuardianPayWindow(service: {
  dos?: unknown;
  end_time?: unknown;
  guardian_signed_at?: unknown;
  esign_completed_at?: unknown;
  guardian_signature_status?: unknown;
} | null | undefined, nowMs = Date.now()): GuardianPayWindow {
  if (String(service?.guardian_signature_status || '').trim().toUpperCase() === 'WAIVED_DOCUMENTED') {
    return clock({ missing: false, isLate: false, isSigned: false, isWaived: true });
  }

  const dateText = String(service?.dos ?? '').trim();
  const timeText = String(service?.end_time ?? '').trim();
  const dateMatch = DATE_RE.exec(dateText);
  const timeMatch = TIME_RE.exec(timeText);
  if (!dateMatch || !timeMatch) {
    return clock({ missing: true, isLate: false, isSigned: false, reason: GUARDIAN_CLOCK_MISSING_MESSAGE });
  }

  const year = Number(dateMatch[1]);
  const month = Number(dateMatch[2]);
  const day = Number(dateMatch[3]);
  const hour = Number(timeMatch[1]);
  const minute = Number(timeMatch[2]);
  const second = Number(timeMatch[3] || '0');
  const calendar = new Date(Date.UTC(year, month - 1, day));
  const dateIsReal = calendar.getUTCFullYear() === year && calendar.getUTCMonth() === month - 1 && calendar.getUTCDate() === day;
  if (!dateIsReal || hour > 23 || minute > 59 || second > 59) {
    return clock({ missing: true, isLate: false, isSigned: false, reason: GUARDIAN_CLOCK_MISSING_MESSAGE });
  }

  const sessionEndIso = `${dateText}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}-07:00`;
  const sessionEndMs = Date.parse(sessionEndIso);
  if (!Number.isFinite(sessionEndMs)) {
    return clock({ missing: true, isLate: false, isSigned: false, reason: GUARDIAN_CLOCK_MISSING_MESSAGE });
  }

  const deadlineMs = sessionEndMs + TWENTY_FOUR_HOURS_MS;
  const deadlineLabel = formatArizonaDeadline(deadlineMs);
  const signedText = String(service?.guardian_signed_at || service?.esign_completed_at || '').trim();
  if (signedText) {
    const signedMs = parseStoredInstant(signedText);
    if (!Number.isFinite(signedMs)) {
      return clock({
        missing: false,
        unreadableSignedAt: true,
        isLate: false,
        isSigned: true,
        sessionEndIso,
        deadlineMs,
        deadlineLabel,
        completedAt: signedText,
        reason: GUARDIAN_SIGNED_AT_UNREADABLE_MESSAGE
      });
    }
    const elapsedHours = Math.round(Math.max(0, (signedMs - sessionEndMs) / (1000 * 60 * 60)) * 10) / 10;
    return clock({
      missing: false,
      isLate: signedMs > deadlineMs,
      isSigned: true,
      sessionEndIso,
      deadlineMs,
      deadlineLabel,
      completedAt: signedText,
      elapsedHours
    });
  }

  const elapsedHours = Math.round(Math.max(0, (nowMs - sessionEndMs) / (1000 * 60 * 60)) * 10) / 10;
  return clock({
    missing: false,
    isLate: nowMs > deadlineMs,
    isSigned: false,
    sessionEndIso,
    deadlineMs,
    deadlineLabel,
    elapsedHours
  });
}

export function guardianRemoteDeadlineSentence(clockInput: { date?: unknown; timeOut?: unknown }): string {
  const window = assessGuardianPayWindow({ dos: clockInput.date, end_time: clockInput.timeOut });
  if (!window.deadlineLabel) {
    return `${GUARDIAN_CLOCK_MISSING_MESSAGE} The deadline is 24 hours after the session end in Arizona time. Sending this request does not start or move it.`;
  }
  return `A guardian acknowledgment is due by ${window.deadlineLabel}. Acknowledgment after that time pays $25 and does not count toward volume. Sending or resending this request does not move the deadline.`;
}
